import cx from 'classnames'
import { Highlight, Language, themes } from 'prism-react-renderer'
import Prism from 'prismjs'
import React from 'react'

// A `Prism` global object is necessary in order to add highlighting support
// for the languages imported below. See:
// https://github.com/FormidableLabs/prism-react-renderer/tree/v1.3.1#faq
;(typeof global !== 'undefined' ? global : window).Prism = Prism

import 'prismjs/components/prism-groovy'
import 'prismjs/components/prism-kotlin'
import 'prismjs/components/prism-ruby'
import 'prismjs/components/prism-swift'
import 'prismjs/components/prism-csharp'
import 'prismjs/components/prism-json'

type AddedLanguage =
  | 'groovy'
  | 'kotlin'
  | 'ruby'
  | 'swift'
  | 'csharp'
  // XML is supported without an import above because `prism-react-renderer`
  // bundles the `markup` language, which XML extends in `prismjs`.
  | 'xml'

// TODO: Customize our light and dark themes to better match our design system
const dittoThemes: Record<string, typeof themes.vsDark> = {
  dittoLight: {
    plain: {
      ...themes.vsLight.plain,
      background: '#fafafa',
    },
    styles: themes.vsLight.styles,
  },
  dittoDark: {
    plain: {
      ...themes.vsDark.plain,
      background: '#262626',
    },
    styles: themes.vsDark.styles,
  },
}

const getTheme = (theme: keyof typeof themes | keyof typeof dittoThemes) => {
  const prismKey = theme as keyof typeof themes
  if (prismKey in themes) {
    return themes[prismKey] ?? themes.vsDark
  } else if (theme in dittoThemes) {
    return dittoThemes[theme] ?? themes.vsDark
  }

  return themes.vsDark
}

export interface CodeHighlightProps {
  /** The code to highlight */
  code: string
  /** The language to highlight the code as */
  language?: Language | AddedLanguage
  /** Whether or not to show line numbers */
  lineNumbers?: boolean
  /** The theme to use for highlighting */
  theme?: keyof typeof themes | keyof typeof dittoThemes
  /** Optional className to be applied to the `pre` tag wrapper element */
  className?: string
  /** Whether or not to round the corners of the code block */
  isRounded?: boolean
}

export const CodeHighlight = ({
  code,
  language,
  lineNumbers,
  theme = 'vsDark',
  className: preClassName,
  isRounded,
}: CodeHighlightProps): React.ReactElement => {
  return (
    <Highlight
      prism={Prism}
      theme={getTheme(theme)}
      code={code}
      language={(language ?? 'bash') as Language}
    >
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <pre
          className={cx(
            className,
            'overflow-x-auto px-3 py-5 text-sm',
            { 'rounded-md': isRounded },
            preClassName,
          )}
          style={style}
        >
          {tokens.map((line, i) => {
            const { className, ...lineProps } = getLineProps({ line, key: i })
            return (
              <div
                key={i}
                className={cx(className, { 'table-row': lineNumbers })}
                {...lineProps}
              >
                {lineNumbers && (
                  <span className="table-cell select-none pr-4 text-right opacity-50">
                    {i + 1}
                  </span>
                )}
                <span className={cx({ 'table-cell': lineNumbers })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token, key })} />
                  ))}
                </span>
              </div>
            )
          })}
        </pre>
      )}
    </Highlight>
  )
}
